import classNames from "classnames";
import React, { type InputHTMLAttributes, forwardRef } from "react";

import Button from "@atoms/Button";

interface IProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  className?: string;
  isError?: boolean;
  errorMessage?: string;
  id?: string;
  children?: React.ReactNode;
  files?: string;
  onClickInputFileButton?: () => void;
}

function Input(
  {
    type = "text",
    checked,
    label,
    className,
    isError = false,
    placeholder = "",
    errorMessage = "",
    id,
    children,
    onClickInputFileButton,
    ...props
  }: IProps,
  ref: React.Ref<HTMLInputElement>,
) {
  const isTypeCheck = type === "checkbox" || type === "radio";

  return (
    <>
      <div
        className={classNames(
          "group flex",
          isTypeCheck ? "w-fit flex-row items-center gap-2 leading-none hover:cursor-pointer" : "flex-col gap-1",
          className,
        )}
      >
        {/* 텍스트류는 라벨이 항상 위, 체크박스/라디오는 order로 인풋을 앞에 배치 */}
        {label ? (
          <label
            className={classNames(
              isTypeCheck && "order-2",
              props.disabled ? ["cursor-not-allowed", isTypeCheck && "text-grey"] : "cursor-pointer",
            )}
            htmlFor={id}
          >
            {label}
          </label>
        ) : null}

        <div className={classNames("relative flex items-center", isTypeCheck && "order-1")}>
          <input
            accept="image/*"
            className={classNames(
              !isTypeCheck && [
                "placeholder:text-grey disabled:text-grey dark:placeholder-dark-grey w-full truncate py-1.5 caret-black dark:caret-white",
                "border-b border-solid border-black dark:border-white",
                (isError || errorMessage) && "border-red! dark:border-red!",
              ],
              isTypeCheck && "cursor-pointer",
              type === "file" && "invisible",
              props.disabled && "cursor-not-allowed",
            )}
            id={id}
            multiple={type === "file"}
            placeholder={placeholder}
            ref={ref}
            type={type}
            {...(isTypeCheck && { checked })}
            {...props}
          />

          {/* 파일 인풋일 때 */}
          {type === "file" ? (
            <label
              className="align-items absolute top-0 left-0 flex size-full w-full cursor-pointer items-center justify-between border border-x-0 border-t-0 border-solid border-black"
              htmlFor={id}
            >
              <span className={classNames(!props?.files && "text-neutral-400")}>{props?.files || placeholder}</span>
              <Button
                as="text"
                id="inputFileButton"
                onClick={() => {
                  if (!ref) return;
                  if (!props?.files) {
                    (ref as React.RefObject<HTMLInputElement>).current?.click();
                    return;
                  }
                  if ((ref as React.RefObject<HTMLInputElement>).current) {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    (ref as React.RefObject<HTMLInputElement>).current!.value = "";
                  }
                  onClickInputFileButton?.();
                }}
              >
                {props?.files ? "×" : "Upload Image"}
              </Button>
            </label>
          ) : null}
          {children ? children : null}
        </div>
      </div>
      {errorMessage ? <span className="text-red font-fuzzy-light -mt-1 leading-none">{errorMessage}</span> : null}
    </>
  );
}

export default forwardRef(Input);
